import React from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormLabel, Grid, Typography } from '@mui/material'
import { FormInput } from '@/components/form'
import { getCreateEmployeeSchema, IUser, User } from '@/@data'
import PhoneCodeSelection from '@/components/selection/PhoneCodeSelection'
import InputDateField from '@/components/common/input/InputDateField'
import { useTranslator } from '@/hooks/useTranslator'
import { useNotification } from '@/hooks/useNotification'
import CompanySelection from '@/components/selection/CompanySelection'
import BankSelection from '@/components/selection/BankSelection'
import { SelectField } from '../../common/input/SelectField'

interface EmployeeEditFormProps {
  employee?: IUser
  onSuccess?: () => void
  refetch?: () => void
}

import dayjs from 'dayjs'

const EmployeeEditForm = React.forwardRef<{ submit: () => void } | null, EmployeeEditFormProps>(
  ({ employee, refetch, onSuccess }, ref) => {
    const { __ } = useTranslator()
    const n = useNotification()

    const methods = useForm<any>({
      defaultValues: {
        first_name: employee?.first_name || '',
        email: employee?.email || '',
        phone_code: employee?.phone_code || '+84',
        phone: employee?.phone || '',
        birth_date: employee?.birth_date || null,
        role_id: employee?.role_id || 0,
        company_id: employee?.company_id || 0,
        employee_id: employee?.employee_id || '',
        contract_type: employee?.contract_type || '',
        bank_account_name: employee?.bank_account_name || '',
        bank_account_number: employee?.bank_account_number || '',
        bank_id: employee?.bank_id || '',
        joined_date: employee?.joined_date || null,
        identity_card: employee?.identity_card || '',
        identity_card_issue_date: employee?.identity_card_issue_date || '',
        identity_card_issue_place: employee?.identity_card_issue_place || '',
        gender: employee?.gender || '',
        dependent: employee?.dependent || '',
        tenure: employee?.tenure || '',
        address: employee?.address || ''
      },
      resolver: yupResolver(getCreateEmployeeSchema(__)),
      mode: 'onChange'
    })

    const onSubmit = async (data: any) => {
      if (!employee) return

      try {
        // Construct payload with status = active
        const payload = {
          ...data
        }

        await User.updateEmployee(employee.id, payload)
        n.success(__('User updated successfully'))
        onSuccess?.()
        if (refetch) refetch()
      } catch (error) {
        n.error(__('Failed to update user'))
      }
    }

    React.useImperativeHandle(ref, () => ({
      submit: () => {
        methods.handleSubmit(onSubmit)()
      },
      reset: () => {
        methods.reset()
      }
    }))

    useEffect(() => {
      if (!employee) return

      methods.reset({
        birth_date: employee.birth_date,
        company_id: employee.company_id || 0,
        bank_id: employee.bank_id || 0,
        bank_account_number: employee.bank_account_number,
        bank_account_name: employee?.bank_account_name || '',
        identity_card: employee?.identity_card || '',
        identity_card_issue_date: employee?.identity_card_issue_date || '',
        identity_card_issue_place: employee?.identity_card_issue_place || '',
        gender: employee?.gender || '',
        address: employee?.address || '',
        dependent: employee?.dependent || '',
        tenure: employee?.tenure || '',
        employee_id: employee?.employee_id || '',
        joined_date: employee?.joined_date || '',
        contract_type: employee?.contract_type || '',
        first_name: employee?.first_name || '',
        email: employee?.email || ''
      })
    }, [employee, methods])

    // Auto-calculate tenure in months whenever joined_date changes
    const joinedDateValue = methods.watch('joined_date')

    React.useEffect(() => {
      if (!joinedDateValue) {
        methods.setValue('tenure', '')

        return
      }

      const months = dayjs().diff(dayjs(joinedDateValue), 'month')

      methods.setValue('tenure', String(months))
    }, [joinedDateValue])

    return (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid size={4}>
              <FormInput
                name='first_name'
                placeholder={__('Employee Name')}
                control={methods.control}
                label={__('Employee Name')}
                fullWidth
                required
                disabled
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name='company_id'
                control={methods.control}
                render={({ field }) => (
                  <CompanySelection
                    {...field}
                    fullWidth
                    label={__('Employer')}
                    onClear={() => methods.setValue('company_id', 0)}
                    disabled
                    required
                    showEndAdornment={false}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <FormInput
                name='employee_id'
                placeholder={__('Employee Code')}
                control={methods.control}
                label={__('Employee Code')}
                fullWidth
                required
                disabled
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name='joined_date'
                control={methods.control}
                render={({ field }) => (
                  <InputDateField
                    value={field.value}
                    placeholder={__('Select Date')}
                    onChange={field.onChange}
                    className='rounded-full py-2 w-full'
                    label={__('Joined Date')}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <FormInput
                name='contract_type'
                placeholder={__('Contract Type')}
                control={methods.control}
                label={__('Contract Type')}
                fullWidth
              />
            </Grid>
            <Grid size={4} gap={2}>
              <Grid size={{ xs: 12, sm: 12 }}>
                <Typography variant='body2' fontWeight={500}>
                  {__('Phone')} <span style={{ color: 'red' }}>*</span>
                </Typography>
              </Grid>
              <Grid container sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
                <Grid size={{ xs: 12, sm: 4 }} sx={{ flex: 1 }}>
                  <Controller
                    name='phone_code'
                    control={methods.control}
                    render={({ field }) => (
                      <PhoneCodeSelection {...field} placeholder={__('+1 (*)')} required disabled />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 7 }} sx={{ flex: 1 }} className='flex-1'>
                  <FormInput
                    name='phone'
                    placeholder={__('Enter Phone')}
                    control={methods.control}
                    required
                    fullWidth
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={4}>
              <FormInput
                name='bank_account_name'
                placeholder={__('Bank Account Name')}
                control={methods.control}
                label={__('Bank Account Name')}
                fullWidth
                required
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name='bank_id'
                control={methods.control}
                render={({ field }) => (
                  <BankSelection
                    {...field}
                    fullWidth
                    label={__('Bank Name')}
                    onClear={() => methods.setValue('bank_id', '')}
                    showEndAdornment={false}
                    wideEndAdornment={true}
                    sx={{
                      '& .MuiSelect-select': {
                        '&.MuiInputBase-input': {
                          paddingInlineEnd: '80px !important'
                        }
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <FormInput
                name='bank_account_number'
                placeholder={__('Bank Account')}
                control={methods.control}
                label={__('Bank Account')}
                type='number'
                fullWidth
                required
              />
            </Grid>
            <Grid size={4}>
              <FormInput
                name='identity_card'
                placeholder={__('Identity Card')}
                control={methods.control}
                label={__('Identity Card')}
                fullWidth
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name='identity_card_issue_date'
                control={methods.control}
                render={({ field }) => (
                  <InputDateField
                    value={field.value}
                    placeholder={__('Select Date')}
                    onChange={field.onChange}
                    className='rounded-full py-2 w-full'
                    label={__('Identity Card Issue Date')}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <FormInput
                name='identity_card_issue_place'
                placeholder={__('Identity Card Issue Place')}
                control={methods.control}
                label={__('Identity Card Issue Place')}
                fullWidth
              />
            </Grid>
            <Grid size={4}>
              <FormLabel>{__('Gender')}</FormLabel>
              <Controller
                name='gender'
                control={methods.control}
                render={({ field }) => (
                  <SelectField
                    {...field}
                    className='w-full py-2'
                    options={[
                      { label: __('Male'), value: 'male' },
                      { label: __('Female'), value: 'female' }
                    ]}
                    displayEmpty
                    renderValue={value => {
                      if (!value) {
                        return <span style={{ color: '#999' }}>{__('Select Gender')}</span>
                      }

                      return value === 'male' ? __('Male') : __('Female')
                    }}
                    fullWidth
                    style={{ backgroundColor: '#fff' }}
                  />
                )}
              />
            </Grid>
            <Grid size={4}>
              <FormInput
                name='address'
                placeholder={__('Address')}
                control={methods.control}
                label={__('Address')}
                fullWidth
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name='birth_date'
                control={methods.control}
                render={({ field }) => (
                  <InputDateField
                    value={field.value}
                    placeholder={__('Select Date')}
                    onChange={field.onChange}
                    className='rounded-full py-2'
                    label={__('D.O.B')}
                  />
                )}
              />
            </Grid>

            <Grid size={4}>
              <FormInput
                name='dependent'
                placeholder={__('Dependent')}
                control={methods.control}
                label={__('Dependent')}
                fullWidth
              />
            </Grid>

            <Grid size={4}>
              <FormInput
                name='tenure'
                placeholder={__('Tenure')}
                control={methods.control}
                label={__('Tenure')}
                fullWidth
                disabled
                slotProps={{
                  input: {
                    endAdornment: <span className='text-[#2F2B3D66] text-[14px] ml-2'>{__('months')}</span>
                  }
                }}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    )
  }
)

export default EmployeeEditForm
